import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  Flex,
  Text,
  TextField,
  Separator,
} from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

/**
 * The JSON structure stored in suppliers.product_template_labels.
 * Keyed by product_requirement_id, each value is a Record<locale, label>.
 */
export type ProductTemplateLabels = Record<
  string,
  Record<string, string>
> | null;

const SUPPORTED_LOCALES = ["en", "fr"] as const;

type ProductRequirementLabelEditorProps = {
  /** The current label overrides JSON (from suppliers.product_template_labels) */
  labels: ProductTemplateLabels;
  /** Map of product_requirement_id -> description, for all requirements in the template */
  requirementDescriptions: Record<string, string>;
  /** Called with the updated labels when user confirms */
  onChange: (labels: ProductTemplateLabels) => void;
  /** Controls whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close */
  onOpenChange: (open: boolean) => void;
};

export function ProductRequirementLabelEditor({
  labels,
  requirementDescriptions,
  onChange,
  open,
  onOpenChange,
}: ProductRequirementLabelEditorProps) {
  const { t } = useTranslation();

  // Local draft state — initialized from labels when dialog opens
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>(
    {},
  );

  // Reset draft when dialog opens
  useEffect(() => {
    if (open) {
      const initial: Record<string, Record<string, string>> = {};
      for (const reqId of Object.keys(requirementDescriptions)) {
        initial[reqId] = {};
        for (const locale of SUPPORTED_LOCALES) {
          initial[reqId][locale] = labels?.[reqId]?.[locale] ?? "";
        }
      }
      setDraft(initial);
    }
  }, [open, labels, requirementDescriptions]);

  const handleChange = (reqId: string, locale: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [reqId]: {
        ...prev[reqId],
        [locale]: value,
      },
    }));
  };

  const handleConfirm = () => {
    // Build result: only include entries that have at least one non-empty label
    const result: Record<string, Record<string, string>> = {};
    let hasAny = false;
    for (const [reqId, locales] of Object.entries(draft)) {
      const nonEmpty: Record<string, string> = {};
      for (const [locale, value] of Object.entries(locales)) {
        if (value.trim()) {
          nonEmpty[locale] = value.trim();
        }
      }
      if (Object.keys(nonEmpty).length > 0) {
        result[reqId] = nonEmpty;
        hasAny = true;
      }
    }
    onChange(hasAny ? result : null);
    onOpenChange(false);
  };

  const reqIds = Object.keys(requirementDescriptions);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>{t("suppliers.labelOverrides")}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          {t("suppliers.labelOverridesDescription")}
        </Dialog.Description>

        <Flex direction="column" gap="4">
          {reqIds.map((reqId, index) => (
            <Flex key={reqId} direction="column" gap="2">
              {index > 0 && <Separator size="4" />}
              <Text size="2" weight="bold">
                {requirementDescriptions[reqId]}
              </Text>
              {SUPPORTED_LOCALES.map((locale) => (
                <Flex key={locale} align="center" gap="2">
                  <Text
                    size="1"
                    style={{ width: 24, textTransform: "uppercase" }}
                    color="gray"
                  >
                    {locale}
                  </Text>
                  <TextField.Root
                    size="2"
                    style={{ flex: 1 }}
                    placeholder={t("suppliers.labelPlaceholder")}
                    value={draft[reqId]?.[locale] ?? ""}
                    onChange={(e) =>
                      handleChange(reqId, locale, e.target.value)
                    }
                  />
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {t("common.cancel")}
            </Button>
          </Dialog.Close>
          <Button onClick={handleConfirm}>{t("common.ok")}</Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
